/**
 * Maintenance Filters - Filter controls for maintenance activities
 */
import { useMemo } from 'react';
import { Paper, Group, Stack, MultiSelect, Checkbox } from '@mantine/core';
import type { MaintenanceActivity } from '@/types/maintenance';
import { getTaskTypeLabel } from '@/types/maintenance';

interface MaintenanceFiltersProps {
  activities: MaintenanceActivity[];
  selectedTaskTypes: string[];
  onTaskTypesChange: (values: string[]) => void;
  selectedTaskTitles: string[];
  onTaskTitlesChange: (values: string[]) => void;
  selectedEquipment: string[];
  onEquipmentChange: (values: string[]) => void;
  performedOnly: boolean;
  onPerformedOnlyChange: (checked: boolean) => void;
}

export function MaintenanceFilters({
  activities,
  selectedTaskTypes,
  onTaskTypesChange,
  selectedTaskTitles,
  onTaskTitlesChange,
  selectedEquipment,
  onEquipmentChange,
  performedOnly,
  onPerformedOnlyChange,
}: MaintenanceFiltersProps) {
  // Extract unique task types from activities
  const taskTypeOptions = useMemo(() => {
    const uniqueTypes = new Set<number>();
    activities.forEach((activity) => {
      uniqueTypes.add(activity.task_type);
    });

    return Array.from(uniqueTypes)
      .sort((a, b) => a - b)
      .map((type) => ({
        value: String(type),
        label: getTaskTypeLabel(type),
      }));
  }, [activities]);

  // Extract unique task titles from activities
  const taskTitleOptions = useMemo(() => {
    const uniqueTitles = new Set<string>();
    activities.forEach((activity) => {
      if (activity.task_title) {
        uniqueTitles.add(activity.task_title);
      }
    });

    return Array.from(uniqueTitles).sort().map((title) => ({
      value: title,
      label: title,
    }));
  }, [activities]);

  // Extract unique equipment names from activities
  const equipmentOptions = useMemo(() => {
    const uniqueEquipment = new Set<string>();
    activities.forEach((activity) => {
      if (activity.equipment_name) {
        uniqueEquipment.add(activity.equipment_name);
      }
    });

    return Array.from(uniqueEquipment).sort().map((name) => ({
      value: name,
      label: name,
    }));
  }, [activities]);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group grow align="flex-start">
          <MultiSelect
            label="Task Types"
            placeholder="All task types"
            data={taskTypeOptions}
            value={selectedTaskTypes}
            onChange={onTaskTypesChange}
            clearable
            searchable
          />

          <MultiSelect
            label="Task Titles"
            placeholder="All tasks"
            data={taskTitleOptions}
            value={selectedTaskTitles}
            onChange={onTaskTitlesChange}
            clearable
            searchable
          />
        </Group>

        <Group grow align="flex-start">
          <MultiSelect
            label="Equipment"
            placeholder="All equipment"
            data={equipmentOptions}
            value={selectedEquipment}
            onChange={onEquipmentChange}
            clearable
            searchable
          />

          <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', paddingBottom: 8 }}>
            <Checkbox
              label="Performed Activities Only"
              checked={performedOnly}
              onChange={(event) => onPerformedOnlyChange(event.currentTarget.checked)}
            />
          </div>
        </Group>
      </Stack>
    </Paper>
  );
}
